import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { CustomGroupItemGroup } from "@dto/customGroup.dto";
import { Button, Collapse, Form } from "antd";
import { NamePath } from "antd/es/form/interface";
import { useCallback, useRef, useState } from "react";
import { GLOB } from 'src/util/Glob';
import { XmButton } from "../button/XmButton";
import './RuleList.less';
import XmRule, { AdditionalApiParams, AdditionalApiPropsContext, AdditionalPayload, ApiPrams, ControlBtn, defaultRule, PropsRule, validRule } from "./XmRule";
import AllItems from "./comp/AllItems";
import AllTags from "./comp/AllTags";
import cgPreviewHandler from "./comp/preview/CgPreviewHandler";
import { usePreviewModal } from "./comp/preview/Preview";

export interface FormRulesInstance {
  hw_type?: string;
  subsystem: string;
  rules: RuleGroup[];
}

export interface EnableItemId {
  item_id: string;
  disabled: boolean;
}

export interface RuleGroup extends Omit<CustomGroupItemGroup, 'items'> {
  items?: EnableItemId[];
}

export interface Props {
  cls: string;
  hw_type?: string;
  subsystem: string;
  mountPoint: NamePath<FormRulesInstance>;// (string | number)[] | string;
  noTagMode?: boolean;
  withoutParents?: boolean;
  additionalApiParams?: AdditionalApiParams & { preview?: (AdditionalApiParams['preview'] & Partial<Record<'all' | 'rule', ApiPrams>>) };
  additionalPayload?: AdditionalPayload;
}

function XmRuleList({
  additionalApiParams,
  additionalPayload,
  mountPoint,
  noTagMode = false,
  withoutParents = false,
  dependenciesPoint: dependenciesPointProp,
  ...rest
}: PropsRule<Props>) {
  const mountPointPath: NamePath<FormRulesInstance> = typeof mountPoint === 'string' ? [mountPoint] : mountPoint;
  const dependenciesPoint = mountPointPath ? typeof mountPointPath[0] === 'number' ? dependenciesPointProp : mountPointPath : undefined;
  const form = Form.useFormInstance<FormRulesInstance>();
  const [showModal, contextHolder] = usePreviewModal();
  const [activeRule, setActiveRule] = useState(0);
  const rulesCounter = useRef<number>(undefined);
  const { readonly } = GLOB.userInfo;
  const handlePreview = (apiParamsType: 'all' | 'rule') => cgPreviewHandler(rest.cls, rest.hw_type, rest.subsystem, showModal, additionalApiParams?.preview?.[apiParamsType]);
  const handleChangeRule = (key: string[]) => {
    if (key.length) {
      setActiveRule(+key.pop());
      return;
    }

    setActiveRule(+key);
  };

  const getRules = useCallback((): RuleGroup[] => form.getFieldValue(dependenciesPoint as never) ?? [], [form, dependenciesPoint]);
  // TODO: specify shouldUpdate
  return (
    <AdditionalApiPropsContext.Provider value={{ ...additionalApiParams, additionalPayload }}>
      <AllTags>
        <AllItems {...rest} noTagMode={noTagMode} linuxExclude={false} additionalApiParams={additionalApiParams?.main}>
          <Form.List name={mountPointPath}>
            {(rules, { add, remove }) => (
              <Form.Item shouldUpdate>
                {() => {
                  if (!rulesCounter.current)
                    rulesCounter.current = rules.length - 1;

                  const disabledBtns = !hasAtLeastOneValidRule(getRules());
                  return <div className="xm-rules-list">
                    <div className="xm-rule-controllers main">
                      <Button size='small' disabled={disabledBtns} onClick={handlePreview('all')(getRules())}>Preview</Button>
                      <XmButton size='small' disabled={!rest.subsystem} onClick={() => {
                        rulesCounter.current++;
                        add({ ...defaultRule });
                        setActiveRule(rulesCounter.current);
                      }}>Add Rule</XmButton>
                    </div>
                    <Collapse onChange={handleChangeRule} activeKey={activeRule} items={rules.map(({ key, name }) => ({
                      key,
                      label: `Rule #${name + 1}`,
                      extra: (
                        <PanelExtra
                          disablePreview={!rest.subsystem || disabledBtns}
                          disableDelete={readonly || rules.length === 1}
                          handleDelete={() => { remove(name); setActiveRule(rules.length); }}
                          handlePreview={handlePreview('rule')([getRules()[name]])}
                        />
                      ),
                      children: (
                        <XmRule
                          withoutParents={withoutParents}
                          mountPoint={[name]}
                          dependenciesPoint={[...dependenciesPoint, name]}
                          {...rest}
                        />
                      ),
                    }))} />
                    {contextHolder}
                  </div>
                }}
              </Form.Item>
            )}
          </Form.List >
        </AllItems>
      </AllTags>
    </AdditionalApiPropsContext.Provider>
  );
}

function hasAtLeastOneValidRule(rules: RuleGroup[]) {
  return rules?.some(validRule);
}

interface PanelExtraProps {
  disableDelete: boolean;
  disablePreview: boolean;
  handlePreview: () => void;
  handleDelete: () => void;
}

function PanelExtra({ disableDelete, disablePreview, handlePreview, handleDelete }: PanelExtraProps) {
  return <div onClick={e => e.stopPropagation()}>
    <ControlBtn
      icon={<EyeOutlined />}
      onClick={handlePreview}
      disabled={disablePreview}
    />
    <ControlBtn
      icon={<DeleteOutlined />}
      onClick={handleDelete}
      className={disableDelete ? "" : "hover-danger"}
      disabled={disableDelete}
    />
  </div>;
}

export default XmRuleList;